import React, { useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import EmailIcon from '@mui/icons-material/Email'
import LightModeIcon from '@mui/icons-material/LightMode'
import LockIcon from '@mui/icons-material/Lock'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Link, useLocation, useNavigate } from 'react-router'
import { PrimaryButton } from '../components/Buttons'
import cnergiLogo from '../resources/CNERGI_PNG@4x.png'
import {
  getRememberedLoginEmail,
  getLoginUserRole,
  loginUser,
  saveRememberedLoginEmail,
  saveLoginSession,
} from '../services/authApi'
import './LoginPage.css'

function LoginPage({ isDarkMode = false, onToggleTheme }) {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const location = useLocation()
  const rememberedEmail = getRememberedLoginEmail()
  const [formValues, setFormValues] = useState({
    email: location.state?.email || rememberedEmail,
    password: '',
    remember: true,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [message, setMessage] = useState('')
  const navigate = useNavigate()

  const handleChange = (event) => {
    const { checked, name, type, value } = event.target

    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (isSubmitting) {
      return
    }

    setIsSubmitting(true)
    setMessage('')

    try {
      const loginResponse = await loginUser({
        email: formValues.email.trim(),
        password: formValues.password,
      })
      const role = getLoginUserRole(loginResponse)

      saveRememberedLoginEmail(formValues.email, formValues.remember)
      saveLoginSession(loginResponse, formValues.remember)
      const destination = role === 'super admin'
        ? '/super-admin'
        : role === 'tech' || role === 'super tech'
          ? '/tech'
          : '/maindash'
      navigate(destination, { replace: true })
    } catch (error) {
      setMessage(error.message || 'Unable to sign in. Please check your email and password.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <main className="login-page">
      <header className="login-page__topbar">
        <a className="login-page__back-link" href="/">
          <ArrowBackIcon aria-hidden="true" />
          <span>Back to home</span>
        </a>

        <button
          className="login-page__theme-toggle"
          type="button"
          aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          onClick={onToggleTheme}
        >
          {isDarkMode ? <LightModeIcon aria-hidden="true" /> : <DarkModeIcon aria-hidden="true" />}
          <span>{isDarkMode ? 'Light' : 'Dark'}</span>
        </button>
      </header>

      <section className="login-page__layout" aria-labelledby="login-title">
        <div className="login-page__panel">
          <a className="login-page__brand" href="/" aria-label="CNERGI home">
            <img src={cnergiLogo} alt="CNERGI" />
            <span>Intelligent Energy. VISIBLE Impact</span>
          </a>

          <div className="login-page__intro">
            <p>CNERGI EMS Portal</p>
            <h1 id="login-title">Sign in to monitor your energy operations.</h1>
          </div>

          <form className="login-page__form" onSubmit={handleSubmit}>
            <label className="login-page__field">
              <span>Email address</span>
              <div className="login-page__input-wrap">
                <EmailIcon aria-hidden="true" />
                <input
                  type="email"
                  name="email"
                  placeholder="you@company.com"
                  autoComplete="email"
                  value={formValues.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </label>

            <label className="login-page__field">
              <span>Password</span>
              <div className="login-page__input-wrap">
                <LockIcon aria-hidden="true" />
                <input
                  type={isPasswordVisible ? 'text' : 'password'}
                  name="password"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  value={formValues.password}
                  onChange={handleChange}
                  required
                />
                <button
                  className="login-page__password-toggle"
                  type="button"
                  aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                  onClick={() => setIsPasswordVisible((current) => !current)}
                >
                  {isPasswordVisible ? <VisibilityOffIcon aria-hidden="true" /> : <VisibilityIcon aria-hidden="true" />}
                </button>
              </div>
            </label>

            <div className="login-page__options">
              <label>
                <input
                  type="checkbox"
                  name="remember"
                  checked={formValues.remember}
                  onChange={handleChange}
                />
                <span>Remember me</span>
              </label>
              <a href="#forgot-password">Forgot password?</a>
            </div>

            <PrimaryButton className="login-page__submit" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </PrimaryButton>

            {message ? (
              <p className="login-page__message login-page__message--error" role="alert" aria-live="polite">
                {message}
              </p>
            ) : null}

            <p className="login-page__register">
              Need an account? <Link to="/register">Create one</Link>
            </p>
          </form>
        </div>

      </section>
    </main>
  )
}

export default LoginPage
